import type { ViewResult } from './domainCalculator';

type OptionalityView = Pick<ViewResult, 'key' | 'remainingDebtYear5' | 'equityInvested'>;

function amount(value: number | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)
    : 'Unbekannt';
}

export function FutureOptionalityPanel({ views, current }: {
  views: readonly OptionalityView[];
  current: boolean;
}) {
  const view = current ? views.find((candidate) => candidate.key === 'ZM-ETF') : undefined;
  return (
    <section className="data-section" aria-labelledby="future-optionality-heading">
      <h3 id="future-optionality-heading">Wohnoptionen in fünf Jahren · Kapitalbindung</h3>
      <p>Was, wenn dann ein passendes Familienheim gebraucht wird? Ein günstiger Vermögensvergleich mit Miete + ETF beantwortet diese Frage nicht.</p>
      <p>Basis: Kaufpfad ZM-ETF mit den eingegebenen Annahmen, keine zusätzliche Prognose.</p>
      {!view ? <p>Für die aktuellen Eingaben liegt noch keine passende Szenarioprognose vor.</p> : null}
      <dl className="fact-list">
        <div>
          <dt>Restschuld nach fünf Jahren (Modell)</dt>
          <dd>{amount(view?.remainingDebtYear5)}</dd>
        </div>
        <div>
          <dt>Beim Kauf eingesetztes Eigenkapital (Startwert, nicht Jahr 5)</dt>
          <dd>{amount(view?.equityInvested)}</dd>
        </div>
        <div>
          <dt>Immobilien-Eigenkapital nach fünf Jahren</dt>
          <dd>Unbekannt – im bestehenden Ergebnis nicht separat ausgewiesen.</dd>
        </div>
        <div>
          <dt>Zusätzliche Kreditkapazität in fünf Jahren</dt>
          <dd>Unbekannt – Restschuld ist weder Kreditlimit noch frei verfügbarer Kreditrahmen.</dd>
        </div>
      </dl>
      <p>Eine hohe absolute Restschuld und laufende Kreditverpflichtungen können Finanzierungsspielraum für ein weiteres Zuhause belegen – unabhängig vom ETF-Ergebnis. Eingesetztes Kapital ist nicht automatisch liquide; auch bilanzielles Immobilien-Eigenkapital ist kein verfügbares Kaufbudget. Ein Verkauf kann Kapital freisetzen, hängt aber von Preis, Kosten und Zeitpunkt ab.</p>
      <p>Zur Einordnung, nicht als Preisgrenze: Bei einem Objekt für 300.000–450.000 € kann ein attraktiver Preis eher Unsicherheit abfedern als bei einem stark fremdfinanzierten Millionenprojekt. Dort können Verluste und gebundenes Kapital die späteren Wohnoptionen stärker einschränken; derselbe relative Preisvorteil bedeutet nicht dieselbe Sicherheitsmarge.</p>
      <p>Unbekannt bleiben künftiges Einkommen, Haushaltsausgaben, weitere Verpflichtungen, Zinsen, Bankprüfung und Preis des späteren Familienheims. Auch ohne Restschuld ist dessen Finanzierbarkeit nicht zugesichert. Keine Risikopunktzahl, Finanzierungszusage oder Anlageempfehlung.</p>
    </section>
  );
}
